// Service card actions router (Telegram sendData + local navigation)
import { getTg } from '../core/telegram.js';
import { getInput, getTextArea } from '../core/dom.js';
import { showView } from './navigation.js';
import { getIsOwnerApp, setIsUpgradeMode } from './app-state.js';
import {
  setOwnerChatActiveConversationUserId,
  setOwnerChatSelectedDisplayName,
  setOwnerChatClearMode,
  setOwnerChatListTab
} from '../features/owner-chat/ui-state.js';
import { openConsultChat } from '../features/orders/order-chat.js';
import { loadPublicPresets } from '../features/presets/list.js';
import { emptyPresetParcel, fillCreateWizardParcelInputs } from '../features/presets/parcel.js';
import { resetCreateDraft } from '../features/presets/create-draft.js';
import { resetPortfolioDraft } from '../features/portfolio/draft.js';
import {
  bindPortfolioKindPicker,
  bindPortfolioRatingPicker,
  setPortfolioRatingPicker
} from '../features/portfolio/list.js';
import { loadOwnerEditPortfolio } from '../features/portfolio/owner-edit.js';
import { loadOwnerEditPresets } from '../features/presets/owner-edit.js';
import { refreshBuildIntroCarousel } from '../features/build/intro.js';
import { handleCustomerActionGate } from './beta-wip.js';

export function sendActionToBot(action: string, label: string): void {
  const t = getTg();
  if (!t) {
    // Не блокируем взаимодействие модальным alert: просто показываем баннер.
    try {
      let el = document.getElementById('telegram-required-banner');
      if (!el) {
        el = document.createElement('div');
        el.id = 'telegram-required-banner';
        el.className = 'alert-banner';
        el.setAttribute('role', 'status');
        el.setAttribute('aria-live', 'polite');
        el.style.position = 'fixed';
        el.style.top = '12px';
        el.style.left = '0';
        el.style.right = '0';
        el.style.zIndex = '10000';
        el.style.margin = '0 16px';
        document.body.appendChild(el);
      }
      el.textContent = 'Откройте мини‑приложение внутри Telegram — без Telegram часть функций недоступна.';
      el.classList.add('alert-banner-visible');
    } catch (_) {}
    // Дальше продолжаем навигацию по локальным экранам.
  }

  if (action === 'ai_assistant') {
    if (!getIsOwnerApp()) return;
    showView('view-ai-assistant');
    return;
  }

  if (!handleCustomerActionGate(action)) return;

  if (action === 'consult') {
    openConsultChat(getIsOwnerApp() ? undefined : 'view-main');
    return;
  }

  if (action === 'build') {
    showView('view-build');
    return;
  }

  if (action === 'build_no') {
    setIsUpgradeMode(false);
    const labelEl = document.querySelector('#view-build-no .form-label');
    const textareaEl = getTextArea('build-tasks');
    const titleEl = document.querySelector('#view-build-no .page-title');
    const subtitleEl = document.querySelector('#view-build-no .page-subtitle');
    if (labelEl) labelEl.textContent = 'ЗАДАЧИ И БЮДЖЕТ';
    if (textareaEl && !textareaEl.value) {
      textareaEl.placeholder = 'ПК для игр, бюджет 30000 RUB...';
    }
    if (titleEl) titleEl.textContent = 'Сборка ПК';
    if (subtitleEl) subtitleEl.textContent = 'Опишите задачи';
    showView('view-build-no');
    return;
  }

  if (action === 'build_yes') {
    showView('view-build-yes');
    return;
  }

  if (action === 'presets') {
    showView('view-presets');
    loadPublicPresets();
    return;
  }

  if (action === 'owner_presets_menu') {
    showView('view-owner-presets-ready', 'right');
    return;
  }

  if (action === 'owner_portfolio_menu') {
    showView('view-owner-portfolio', 'right');
    return;
  }

  if (action === 'owner_chat_menu') {
    setOwnerChatClearMode(false);
    setOwnerChatListTab('order');
    showView('view-owner-chat-list', 'right');
    return;
  }

  if (action === 'owner_chat_clear_menu') {
    setOwnerChatClearMode(true);
    showView('view-owner-chat-list', 'right');
    return;
  }

  if (action === 'owner_infra_status') {
    if (!getIsOwnerApp()) return;
    showView('view-owner-infra-status', 'right');
    return;
  }

  if (action === 'portfolio_create') {
    resetPortfolioDraft({});
    const pt = getInput('portfolio-step-title');
    const pb = getInput('portfolio-step-budget');
    const pd = getTextArea('portfolio-step-desc');
    const pi = getInput('portfolio-step-image');
    if (pt) pt.value = '';
    if (pb) pb.value = '';
    if (pd) pd.value = '';
    if (pi) pi.value = '';
    ['portfolio-step-cpu', 'portfolio-step-gpu', 'portfolio-step-ram', 'portfolio-step-storage', 'portfolio-step-case'].forEach(function (id) {
      const el = getInput(id);
      if (el) el.value = '';
    });
    const ra = getInput('portfolio-step-review-author');
    const rt = getTextArea('portfolio-step-review-text');
    if (ra) ra.value = '';
    if (rt) rt.value = '';
    bindPortfolioKindPicker();
    bindPortfolioRatingPicker();
    setPortfolioRatingPicker(5);
    const kindWrap = document.getElementById('portfolio-step-kind-picker');
    if (kindWrap) {
      kindWrap.querySelectorAll('.portfolio-kind-chip').forEach(function (b) {
        b.classList.toggle('is-active', (b.getAttribute('data-kind') || 'custom') === 'custom');
      });
    }
    showView('view-portfolio-create-1', 'right');
    return;
  }

  if (action === 'portfolio_edit') {
    showView('view-owner-portfolio-edit', 'right');
    loadOwnerEditPortfolio();
    return;
  }

  if (action === 'owner_create') {
    const emptyParcel = emptyPresetParcel();
    resetCreateDraft({ parcel: emptyParcel });
    const createTitle = getInput('create-step-title');
    const createSubtitle = getInput('create-step-subtitle');
    const d = getTextArea('create-step-desc');
    const cpu = getInput('create-step-cpu');
    const gpu = getInput('create-step-gpu');
    const motherboard = getInput('create-step-motherboard');
    const ram = getInput('create-step-ram');
    const psu = getInput('create-step-psu');
    const storage = getInput('create-step-storage');
    const caseName = getInput('create-step-case');
    const p = getInput('create-step-price');
    const oldP = getInput('create-step-old-price');
    const a = getInput('create-step-avito');
    const i = getInput('create-step-image');
    if (createTitle) createTitle.value = '';
    if (createSubtitle) createSubtitle.value = '';
    if (d) d.value = '';
    if (cpu) cpu.value = '';
    if (gpu) gpu.value = '';
    if (motherboard) motherboard.value = '';
    if (ram) ram.value = '';
    if (psu) psu.value = '';
    if (storage) storage.value = '';
    if (caseName) caseName.value = '';
    if (p) p.value = '';
    if (oldP) oldP.value = '';
    if (a) a.value = '';
    if (i) i.value = '';
    fillCreateWizardParcelInputs(emptyParcel);
    showView('view-owner-create-1');
    return;
  }

  if (action === 'owner_edit') {
    showView('view-owner-edit');
    loadOwnerEditPresets();
    return;
  }

  if (action === 'upgrade') {
    setIsUpgradeMode(true);
    const upLabelEl = document.querySelector('#view-build-no .form-label');
    const upTextareaEl = getTextArea('build-tasks');
    const upTitleEl = document.querySelector('#view-build-no .page-title');
    const upSubtitleEl = document.querySelector('#view-build-no .page-subtitle');
    if (upLabelEl) upLabelEl.textContent = 'ЧТО ХОТИТЕ УЛУЧШИТЬ';
    if (upTextareaEl && !upTextareaEl.value) {
      upTextareaEl.placeholder = 'Улучшить GPU, добавить ОЗУ...';
    }
    if (upTitleEl) upTitleEl.textContent = 'Апгрейд ПК';
    if (upSubtitleEl) upSubtitleEl.textContent = 'Опишите, что нужно улучшить';
    showView('view-build-no');
    return;
  }

  if (!action) {
    return;
  }

  const payload = { action, label };
  if (t && typeof t.sendData === 'function') t.sendData(JSON.stringify(payload));
}
